import { useEffect, useRef, useState } from "react";

import { useTheme, type ThemePreference } from "../lib/theme";

// Inline icons — kept local; nothing else needs them. 16px, stroke
// inherits currentColor so they sit in the ink scale like the text.
function ThemeIcon({ kind }: { kind: ThemePreference }) {
  const common = {
    width: 16,
    height: 16,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.6,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };
  if (kind === "light") {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
      </svg>
    );
  }
  if (kind === "dark") {
    return (
      <svg {...common}>
        <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
      </svg>
    );
  }
  // system
  return (
    <svg {...common}>
      <rect x="3" y="4" width="18" height="12" rx="1.5" />
      <path d="M8 20h8M12 16v4" />
    </svg>
  );
}

/**
 * Light / Dark / System selector. Lives in the signed-in SiteHeader and
 * on the public landing page so the choice is reachable everywhere.
 * Reads/writes the theme through useTheme(); the ThemeProvider in
 * __root.tsx wraps the whole app, so this works on every surface.
 */
export function ThemeMenu() {
  const { theme, setTheme, resolved } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Same dismiss-on-outside-click + Escape pattern as the other menus.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const options: { value: ThemePreference; label: string }[] = [
    { value: "light", label: "Light" },
    { value: "dark", label: "Dark" },
    { value: "system", label: "System" },
  ];

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="inline-flex items-center justify-center rounded text-ink-500 hover:text-ink-900 hover:bg-ink-100 min-h-[2.75rem] min-w-[2.75rem] px-2 transition-colors"
        title={`Theme: ${theme}`}
      >
        {/* The button shows what's actually on screen, so "System"
            reads as a sun or moon depending on the OS. */}
        <ThemeIcon kind={resolved} />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 w-44 rounded border border-ink-200 bg-paper shadow-lg z-20"
        >
          <ul>
            {options.map((opt) => {
              const isActive = opt.value === theme;
              return (
                <li key={opt.value}>
                  <button
                    role="menuitemradio"
                    aria-checked={isActive}
                    onClick={() => {
                      setTheme(opt.value);
                      setOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2.5 flex items-center gap-2.5 hover:bg-ink-100 transition-colors ${
                      isActive ? "bg-ink-100" : ""
                    }`}
                  >
                    <span className="shrink-0 text-ink-400">
                      <ThemeIcon kind={opt.value} />
                    </span>
                    <span className="flex-1 text-ink-900 text-sm">
                      {opt.label}
                    </span>
                    {isActive && (
                      <span aria-hidden className="text-accent text-xs">
                        ✓
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
